<template>
    <div class="flex items-center space-x-2">
        <UBadge v-if="!subscription" color="gray" label="Unsubscribed" variant="solid" />
        <UTooltip v-else :ui="{ base: 'h-6 px-0 py-0' }">
            <template #text>
                <div class="flex items-center h-full">
                    <div class="py-1 px-2">
                        <span>{{ sourceType }}</span>
                    </div>
                    <div v-if="!loaded" class="flex items-center bg-gray-50 border-s px-2 h-full">
                        <UIcon name="i-ph-circle-dashed" class="size-3 text-gray-700 animate-spin-slow" />
                    </div>
                    <div v-else-if="subscriptionSource" class="flex items-center bg-gray-50 border-s px-2 h-full">
                        <span class="text-xs font-medium font-mono">{{ subscriptionSource }}</span>
                    </div>
                </div>
            </template>
            <div @mouseenter="fetchSubscriptionSource()">
                <UBadge v-if="isExpired" color="red" label="Expired" variant="subtle" />
                <UBadge v-else color="blue" label="Active" variant="subtle" />
            </div>
        </UTooltip>

        <BadgeSubscriptionExpiry v-if="withExpiry" :subscription="subscription" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { SubscriptionSourceType, type Subscription } from '~/types/subscription';

const props = defineProps<{
    subscription?: Subscription
    withExpiry?: boolean
}>()

const client = useSupabaseClient()
const loaded = ref<boolean>(false)
const subscriptionSource = ref<string>()

const sourceType = computed(() => {
    return props.subscription?.source_type === SubscriptionSourceType.coupon ? "Coupon" : "Credential"
})

const isExpired = computed(() => {
    return props.subscription && dayjs(props.subscription.expires_at).diff(dayjs(), 'days') < 0
})

async function fetchSubscriptionSource() {
    if (props.subscription?.source_id && !loaded.value) {
        if (props.subscription.source_type === SubscriptionSourceType.coupon) {
            const { data } = await client.from('coupons')
                .select('code')
                .eq('id', props.subscription.source_id)
                .is('deleted_at', null)
                .maybeSingle()

            if (data) {
                subscriptionSource.value = data?.code
            }
        }

        if (props.subscription.source_type === SubscriptionSourceType.credential) {
            const { data } = await client.from('credentials')
                .select('fields')
                .eq('id', props.subscription.source_id)
                .maybeSingle()

            if (data) {
                subscriptionSource.value = (data?.fields as any)?.beneficiary_pin
            }
        }
    }

    loaded.value = true
}
</script>